/**
 * Phase 6 cross-cutting PR 3 — bug-report payload builder.
 *
 * **The privacy guarantee of this whole feature lives in this file.**
 *
 * Vanilla `bug-report.js:87-97` transmits the FULL raw
 * `state.athleteContext` object in the payload. That object carries
 * PII and sensitive health data (Name, Age, Gender, weekly mileage,
 * RPE history, body metrics, location, PAR-Q answers, etc.) that a
 * bug report should never include.
 *
 * This builder enforces a **LOCKED 5-key safe subset** of
 * `athleteContext` (per OVERRIDES.md D1):
 *
 *   - `_journey`               (which onboarding journey was selected)
 *   - `_journey_set_at`        (ISO date the journey was locked)
 *   - `_onboarding_completed`  (boolean primary gate flag)
 *   - `_onboarding_completed_at` (ISO date · companion to the boolean)
 *   - `_parq_completed`        (boolean PAR-Q screener flag)
 *
 * The unit test on this file (`buildBugPayload.test.ts`) asserts the
 * `athleteContext` keys set is EXACTLY this 5-key subset regardless
 * of what's in the source object. Adding a 6th key to the safe
 * subset fails the test. This is intentional — expanding the subset
 * requires an explicit OVERRIDES.md update + security/privacy review.
 *
 * Sourced from
 *   - `.claude/references/phase-6-cross-cutting-design/OVERRIDES.md` D1
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §2 DEVIATION
 *   - `.claude/references/phase-6-cross-cutting-design/HANDOFF.md` PR 3
 */

export const BUG_REPORT_SAFE_ATHLETE_CONTEXT_KEYS = [
  '_journey',
  '_journey_set_at',
  '_onboarding_completed',
  '_onboarding_completed_at',
  '_parq_completed',
] as const;

export type BugReportSeverity = 'low' | 'medium' | 'high' | 'critical';

/** What the safe athlete-context subset looks like in the payload. */
export interface SafeAthleteContextSubset {
  _journey: string | null;
  _journey_set_at: string | null;
  _onboarding_completed: boolean | null;
  _onboarding_completed_at: string | null;
  _parq_completed: boolean | null;
}

export interface BugReportPayload {
  title: string;
  description: string;
  severity: BugReportSeverity;
  currentPage: string;
  userAgent: string;
  screenSize: string;
  userRole: string | null;
  userJourney: string | null;
  athleteContext: SafeAthleteContextSubset;
}

export interface BuildBugPayloadInput {
  title: string;
  description: string;
  severity: BugReportSeverity;
  /** Raw athlete-context map from `useAthleteContext().raw` — will be redacted. */
  athleteContext: Record<string, unknown> | null;
  /** Optional auth user role from `useAuthContext().user.user_metadata`. */
  userRole?: string | null;
  /**
   * Optional explicit current page (defaults to `window.location.pathname`).
   * Tests can pass an explicit value to avoid window globals.
   */
  currentPage?: string;
}

/**
 * Build the bug-report POST body, redacting `athleteContext` down to
 * the locked 5-key safe subset.
 *
 * Any key not in `BUG_REPORT_SAFE_ATHLETE_CONTEXT_KEYS` is dropped.
 * Missing keys are filled with `null` so the server contract is
 * stable regardless of which fields the user has populated.
 *
 * @param input - title, description, severity, and the raw athlete context
 * @returns the body for `POST /api/bug-report`
 */
export function buildBugPayload(input: BuildBugPayloadInput): BugReportPayload {
  const raw = input.athleteContext ?? {};
  // Build the safe subset key-by-key so the resulting object has
  // EXACTLY the 5 allowed keys — never any others. This is the heart
  // of the privacy guarantee; do not refactor into a permissive
  // spread or filter.
  const safeAthleteContext: SafeAthleteContextSubset = {
    _journey: typeof raw._journey === 'string' ? raw._journey : null,
    _journey_set_at:
      typeof raw._journey_set_at === 'string' ? raw._journey_set_at : null,
    _onboarding_completed:
      typeof raw._onboarding_completed === 'boolean' ? raw._onboarding_completed : null,
    _onboarding_completed_at:
      typeof raw._onboarding_completed_at === 'string' ? raw._onboarding_completed_at : null,
    _parq_completed:
      typeof raw._parq_completed === 'boolean' ? raw._parq_completed : null,
  };

  return {
    title: input.title,
    description: input.description,
    severity: input.severity,
    currentPage:
      typeof input.currentPage === 'string'
        ? input.currentPage
        : typeof window !== 'undefined'
          ? window.location.pathname
          : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    screenSize:
      typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    userRole: input.userRole ?? null,
    userJourney: typeof raw._journey === 'string' ? raw._journey : null,
    athleteContext: safeAthleteContext,
  };
}
